import { useEffect, useState } from 'react';

import {
  Badge,
  IconButton,
  OffsetPagination,
  Table,
  TableCard,
  TableSkeleton,
  Text,
} from '@opositandopn/ui';
import { IoEaselOutline } from 'react-icons/io5';

import { ResourceDialog } from '../../../../components/shared/resource-dialog/ResourceDialog';
import { BaseComponentProps } from '../../../../types/base-component.types';
import { formatSortDate } from '../../../../utils/date';
import { useGetDiagrams } from '../../api/useDiagrams';
import { DiagramListFilterForm } from '../diagram-list-filter/DiagramListFilter';
import { DiagramView } from '../diagram-view/DiagramView';

import styles from './DiagramAdminList.module.scss';

type ContainerProps = BaseComponentProps & {
  filter: DiagramListFilterForm;
};

function DiagramAdminList({
  className,
  filter,
  testId = 'ui-diagram-admin-list',
}: ContainerProps) {
  const [current, setCurrent] = useState<string>();
  const [pagination, setPagination] = useState({ pageNumber: 1, pageSize: 20 });

  const { data: response, isLoading } = useGetDiagrams({
    subjectId: filter.subject.id,
    pageNumber: pagination.pageNumber,
    pageSize: pagination.pageSize,
    fromTime: filter.fromTime,
    toTime: filter.toTime,
    sort: filter.sort,
    params: {
      unitId: filter.unit?.id,
      lessonId: filter.lesson?.id,
      tagIds: filter.tags?.map((tag) => tag.id),
      text: filter.text,
      visibilities: filter.visibilities,
    },
  });

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
  }, [filter]);

  return (
    <>
      <TableCard.Root className={className} data-testid={testId}>
        <TableCard.Header
          title="Esquemas"
          contentTrailing={
            <Badge>
              {response?.totalItems
                ? `Total: ${response?.totalItems}`
                : 'Sin esquemas'}
            </Badge>
          }
        />
        <Table aria-label="Esquemas">
          <Table.Header>
            <Table.Head id="lesson" label="Lección" isRowHeader />
            <Table.Head id="name" label="Nombre" />
            <Table.Head id="createdAt" label="Fecha de creación" />
            <Table.Head id="actions" />
          </Table.Header>
          {isLoading || response === undefined ? (
            <TableSkeleton columns={4} items={pagination.pageSize} />
          ) : (
            <Table.Body
              items={response.items}
              renderEmptyState={() => 'No hay esquemas'}
            >
              {(item) => (
                <Table.Row id={item.id}>
                  <Table.Cell
                    style={{
                      width: '20%',
                      wordWrap: 'break-word',
                      textWrap: 'balance',
                      whiteSpace: 'normal',
                    }}
                  >
                    {item.lesson?.name}
                  </Table.Cell>
                  <Table.Cell
                    style={{
                      width: '100%',
                      wordWrap: 'break-word',
                      whiteSpace: 'normal',
                    }}
                  >
                    {item.name}
                  </Table.Cell>
                  <Table.Cell>
                    <Text
                      font="monospace"
                      variety="annotation"
                      variant="neutral"
                    >
                      {formatSortDate(new Date(item.createdAt))}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <div className={styles['actions']}>
                      <IconButton
                        size="sm"
                        icon={<IoEaselOutline />}
                        onClick={() => {
                          setCurrent(item.id);
                        }}
                      />
                    </div>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          )}
        </Table>
        {response && (
          <OffsetPagination
            className={styles.pagination}
            pageNumber={pagination.pageNumber}
            pageSize={pagination.pageSize}
            totalRecords={response.totalItems}
            onPageChange={(pageNumber: number) => {
              setPagination({ ...pagination, pageNumber });
            }}
            pageNeighbours={2}
          />
        )}
      </TableCard.Root>
      <ResourceDialog
        open={current !== undefined}
        className={styles['image-viewer']}
      >
        {current && (
          <DiagramView
            subjectId={filter.subject.id}
            diagramId={current}
            onClose={() => {
              setCurrent(undefined);
            }}
          />
        )}
      </ResourceDialog>
    </>
  );
}

export { DiagramAdminList };
